import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Palette } from 'lucide-react';
import { getMyAlertStyle, saveAlertStyle } from '../../../api/payment/alert-style.api';
import { sendTestAlert } from '../../../api/payment/donate-alert-tiers.api';
import DonateAlertView from '../../../components/donate-alert/donate-alert-view';
import {
  ALERT_POSITIONS,
  AlertAnimation,
  AlertLayout,
  AlertPosition,
  AlertStyle,
  AlertTextSize,
  DEFAULT_ALERT_STYLE,
  normalizeAlertStyle,
} from '../../../utils/alert-style';
import { renderTemplate } from '../../../utils/donate-alert';

const BTN_SECONDARY =
  'h-10 rounded-lg border border-[#C8CCD8] dark:border-slate-600 px-3 text-sm text-gray-700 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed';
const CHIP_BASE = 'h-10 rounded-lg border px-3 text-sm transition-colors';
const CHIP_ON = 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-200';
const CHIP_OFF =
  'border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200';

// Khung ảo của bản xem trước (tỉ lệ 16:9), thu nhỏ bằng transform cho vừa bề rộng khung.
const VIRTUAL_W = 1280;
const VIRTUAL_H = 720;
const PREVIEW_GIF = '/gif/img_1.gif'; // ảnh mặc định của overlay
const PREVIEW_COLOR = 'red'; // màu chữ mặc định của overlay
const PREVIEW_TEXT = renderTemplate(null, { ten: 'Minh', soTien: 50000 });
const PREVIEW_MESSAGE = 'Chúc anh em buổi live thật vui!';

const LAYOUTS: { value: AlertLayout; label: string }[] = [
  { value: 'classic', label: 'Cổ điển' },
  { value: 'card', label: 'Thẻ ngang' },
  { value: 'banner', label: 'Biểu ngữ' },
  { value: 'minimal', label: 'Tối giản' },
  { value: 'glow', label: 'Viền sáng' },
  { value: 'poster', label: 'Khung lớn' },
];
const TEXT_SIZES: { value: AlertTextSize; label: string }[] = [
  { value: 'sm', label: 'Nhỏ' },
  { value: 'md', label: 'Vừa' },
  { value: 'lg', label: 'Lớn' },
];
const ANIMATIONS: { value: AlertAnimation; label: string }[] = [
  { value: 'fade', label: 'Mờ dần' },
  { value: 'slide', label: 'Trượt vào' },
  { value: 'zoom', label: 'Phóng to' },
  { value: 'bounce', label: 'Nảy' },
];
const POSITION_LABELS: Record<AlertPosition, string> = {
  'top-left': 'Góc trên bên trái',
  'top-center': 'Giữa phía trên',
  'top-right': 'Góc trên bên phải',
  'middle-left': 'Giữa bên trái',
  'middle-center': 'Chính giữa',
  'middle-right': 'Giữa bên phải',
  'bottom-left': 'Góc dưới bên trái',
  'bottom-center': 'Giữa phía dưới',
  'bottom-right': 'Góc dưới bên phải',
};
// Chấm trong ô vị trí: căn theo đúng góc của ô.
const DOT_PLACE: Record<AlertPosition, string> = {
  'top-left': 'items-start justify-start',
  'top-center': 'items-start justify-center',
  'top-right': 'items-start justify-end',
  'middle-left': 'items-center justify-start',
  'middle-center': 'items-center justify-center',
  'middle-right': 'items-center justify-end',
  'bottom-left': 'items-end justify-start',
  'bottom-center': 'items-end justify-center',
  'bottom-right': 'items-end justify-end',
};

const CHECKER: React.CSSProperties = {
  backgroundColor: '#1f2937',
  backgroundImage:
    'linear-gradient(45deg, #374151 25%, transparent 25%), linear-gradient(-45deg, #374151 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #374151 75%), linear-gradient(-45deg, transparent 75%, #374151 75%)',
  backgroundSize: '20px 20px',
  backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0',
};

/** Hình minh hoạ nhỏ cho từng kiểu khung (chỉ div + Tailwind). */
const LayoutThumb: React.FC<{ layout: AlertLayout }> = ({ layout }) => {
  const img = 'bg-amber-400 rounded-sm';
  const line = 'h-1 rounded-full';
  switch (layout) {
    case 'card':
      return (
        <div className="flex items-center gap-1 rounded bg-black/60 px-1.5 py-1">
          <div className={`${img} h-4 w-4`} />
          <div className="flex flex-col gap-0.5">
            <div className={`${line} w-8 bg-red-500`} />
            <div className={`${line} w-6 bg-white`} />
          </div>
        </div>
      );
    case 'banner':
      return (
        <div className="flex w-full items-center justify-center gap-1 bg-black/60 py-1">
          <div className={`${img} h-3 w-3`} />
          <div className="flex flex-col gap-0.5">
            <div className={`${line} w-8 bg-red-500`} />
            <div className={`${line} w-6 bg-white`} />
          </div>
        </div>
      );
    case 'minimal':
      return (
        <div className="flex flex-col items-center gap-1">
          <div className={`${line} w-10 bg-red-500`} />
          <div className={`${line} w-7 bg-white`} />
        </div>
      );
    case 'glow':
      return (
        <div className="flex flex-col items-center gap-0.5 rounded border border-red-500 bg-black/60 px-2 py-1 shadow-[0_0_6px_#ef4444]">
          <div className={`${img} h-3 w-3`} />
          <div className={`${line} w-7 bg-red-500`} />
        </div>
      );
    case 'poster':
      return (
        <div className="relative h-9 w-12 overflow-hidden rounded bg-amber-400">
          <div className="absolute inset-x-0 bottom-0 flex h-1/2 flex-col justify-end gap-0.5 bg-gradient-to-t from-black/80 to-transparent px-1 pb-1">
            <div className={`${line} w-7 bg-red-500`} />
            <div className={`${line} w-5 bg-white`} />
          </div>
        </div>
      );
    default:
      return (
        <div className="flex flex-col items-center gap-0.5">
          <div className={`${img} h-4 w-4`} />
          <div className={`${line} w-9 bg-red-500`} />
          <div className={`${line} w-6 bg-white`} />
        </div>
      );
  }
};

/**
 * Giao diện thông báo donate trên OBS: kiểu khung, vị trí, cỡ chữ, hiệu ứng, nền thẻ.
 * Một giao diện chung cho mọi thông báo; mốc hiệu ứng vẫn giữ âm thanh, ảnh, màu chữ riêng.
 */
const AlertStyleSetting: React.FC<{ userId?: string }> = ({ userId }) => {
  const [style, setStyle] = useState<AlertStyle>(DEFAULT_ALERT_STYLE);
  const [saved, setSaved] = useState<AlertStyle>(DEFAULT_ALERT_STYLE);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const [scale, setScale] = useState(0.5);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const load = () => {
    setLoadError(false);
    getMyAlertStyle()
      .then((res) => {
        const s = normalizeAlertStyle(res);
        setStyle(s);
        setSaved(s);
        setLoaded(true);
        setAnimKey((k) => k + 1);
      })
      .catch(() => setLoadError(true));
  };

  useEffect(() => {
    if (!userId) return;
    load();
  }, [userId]);

  // Thu nhỏ khung ảo 1280×720 cho vừa bề rộng khung xem trước (đổi theo cỡ màn hình).
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const fit = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / VIRTUAL_W);
    };
    fit();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', fit);
      return () => window.removeEventListener('resize', fit);
    }
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const update = (patch: Partial<AlertStyle>, replay = true) => {
    setStyle((prev) => ({ ...prev, ...patch }));
    if (replay) setAnimKey((k) => k + 1);
  };

  const same = (a: AlertStyle, b: AlertStyle) =>
    a.layout === b.layout &&
    a.position === b.position &&
    a.textSize === b.textSize &&
    a.animation === b.animation &&
    a.cardColor.toLowerCase() === b.cardColor.toLowerCase() &&
    a.cardOpacity === b.cardOpacity;
  const isDirty = !same(style, saved);

  const handleSave = () => {
    if (saving || !loaded) return;
    setSaving(true);
    saveAlertStyle(style)
      .then((res) => {
        const s = normalizeAlertStyle(res);
        setStyle(s);
        setSaved(s);
        toast.success('Đã lưu giao diện thông báo');
      })
      .catch((err) => toast.error(err?.response?.data?.message || 'Lưu không thành công'))
      .finally(() => setSaving(false));
  };

  const testOnObs = () => {
    if (isDirty) {
      toast.error('Bạn lưu thay đổi trước rồi gửi thử nhé');
      return;
    }
    if (sending) return;
    setSending(true);
    sendTestAlert()
      .then(() => toast.success('Đã gửi, xem trên OBS nhé'))
      .catch((err) => toast.error(err?.response?.data?.message || 'Gửi thử không thành công'))
      .finally(() => setSending(false));
  };

  const usesCard = style.layout !== 'classic' && style.layout !== 'minimal';

  return (
    <div className="border rounded-xl border-[#DEE0E6] dark:border-slate-700 mt-[10px]">
      <p className="p-3 flex items-center gap-2 w-full text-[#18191E] dark:text-white font-semibold text-[22px] leading-[27px]">
        <Palette size={20} className="text-blue-600" />
        Giao diện thông báo
      </p>

      <div className="w-full flex flex-col gap-4 bg-[#f6f7f8] dark:bg-dark2 p-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Chọn kiểu khung, vị trí và hiệu ứng cho thông báo donate hiện trên OBS. Mọi thông báo dùng chung giao
          diện này, còn âm thanh, ảnh động và màu chữ vẫn theo từng mốc như cũ.
        </p>

        {loadError && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
            <span>Không tải được giao diện đã lưu, bạn thử lại nhé.</span>
            <button type="button" className="font-medium underline" onClick={load}>
              Thử lại
            </button>
          </div>
        )}

        {/* Xem trước */}
        <div className="flex flex-col gap-2">
          <div
            ref={boxRef}
            className="relative w-full aspect-video overflow-hidden rounded-lg border border-[#C8CCD8] dark:border-slate-600"
            style={CHECKER}
          >
            <div
              className="absolute left-0 top-0 pointer-events-none"
              style={{
                width: VIRTUAL_W,
                height: VIRTUAL_H,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            >
              <DonateAlertView
                gif={PREVIEW_GIF}
                text={PREVIEW_TEXT}
                message={PREVIEW_MESSAGE}
                color={PREVIEW_COLOR}
                style={style}
                animKey={animKey}
                frame={{ width: VIRTUAL_W, height: VIRTUAL_H }}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={BTN_SECONDARY} onClick={() => setAnimKey((k) => k + 1)}>
              Xem lại hiệu ứng
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Khung xem trước mô phỏng màn hình 1280×720, nền caro là phần trong suốt trên OBS.
            </span>
          </div>
        </div>

        {/* Kiểu khung */}
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-[#18191E] dark:text-white">Kiểu khung</span>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {LAYOUTS.map((l) => {
              const on = style.layout === l.value;
              return (
                <button
                  key={l.value}
                  type="button"
                  aria-pressed={on}
                  onClick={() => update({ layout: l.value })}
                  className={`flex flex-col items-center gap-2 rounded-lg border p-2 text-sm ${on ? CHIP_ON : CHIP_OFF}`}
                >
                  <div className="flex h-14 w-full items-center justify-center overflow-hidden rounded bg-slate-700">
                    <LayoutThumb layout={l.value} />
                  </div>
                  {l.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Vị trí */}
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-[#18191E] dark:text-white">
            Vị trí trên màn hình: {POSITION_LABELS[style.position].toLowerCase()}
          </span>
          <div className="grid w-44 grid-cols-3 gap-1 rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-800 p-1">
            {ALERT_POSITIONS.map((p) => {
              const on = style.position === p;
              return (
                <button
                  key={p}
                  type="button"
                  aria-label={POSITION_LABELS[p]}
                  aria-pressed={on}
                  title={POSITION_LABELS[p]}
                  onClick={() => update({ position: p })}
                  className={`flex h-9 rounded p-1.5 ${DOT_PLACE[p]} ${
                    on ? 'bg-blue-600' : 'bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600'
                  }`}
                >
                  <span className={`block h-2.5 w-2.5 rounded-sm ${on ? 'bg-white' : 'bg-gray-400'}`} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Cỡ chữ + hiệu ứng */}
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-[#18191E] dark:text-white">Cỡ chữ</span>
          <div className="flex flex-wrap gap-2">
            {TEXT_SIZES.map((t) => (
              <button
                key={t.value}
                type="button"
                aria-pressed={style.textSize === t.value}
                onClick={() => update({ textSize: t.value }, false)}
                className={`${CHIP_BASE} ${style.textSize === t.value ? CHIP_ON : CHIP_OFF}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-[#18191E] dark:text-white">Hiệu ứng xuất hiện</span>
          <div className="flex flex-wrap gap-2">
            {ANIMATIONS.map((a) => (
              <button
                key={a.value}
                type="button"
                aria-pressed={style.animation === a.value}
                onClick={() => update({ animation: a.value })}
                className={`${CHIP_BASE} ${style.animation === a.value ? CHIP_ON : CHIP_OFF}`}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        {/* Nền thẻ */}
        <div className={`flex flex-col gap-2 ${usesCard ? '' : 'opacity-60'}`}>
          <span className="text-sm font-medium text-[#18191E] dark:text-white">Nền thẻ</span>
          <div className="flex flex-wrap items-center gap-4 text-sm dark:text-gray-200">
            <label className="flex items-center gap-2">
              Màu nền
              <input
                type="color"
                value={style.cardColor}
                onChange={(e) => update({ cardColor: e.target.value }, false)}
                className="h-9 w-12 rounded border border-[#C8CCD8] dark:border-slate-600"
              />
            </label>
            <label className="flex min-w-[220px] flex-1 flex-col gap-1">
              Độ mờ nền: {style.cardOpacity}%
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={style.cardOpacity}
                onChange={(e) =>
                  update(
                    { cardOpacity: Math.min(100, Math.max(0, Math.round(Number(e.target.value) || 0))) },
                    false
                  )
                }
                className="accent-blue-600"
              />
            </label>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            0 là trong suốt, 100 là nền đặc. Dùng cho Thẻ ngang, Biểu ngữ, Viền sáng và Khung lớn.
          </span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <button type="button" className={BTN_SECONDARY} disabled={sending} onClick={testOnObs}>
              {sending ? 'Đang gửi…' : 'Gửi thử lên OBS'}
            </button>
            <button type="button" className={BTN_SECONDARY} onClick={() => update({ ...DEFAULT_ALERT_STYLE })}>
              Về kiểu mặc định
            </button>
          </div>
          <button
            type="button"
            disabled={saving || !loaded}
            onClick={handleSave}
            className="flex h-12 items-center gap-1 rounded-lg bg-[#39539E] px-6 text-white disabled:opacity-60"
          >
            {saving ? 'Đang lưu…' : 'Lưu giao diện'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertStyleSetting;
